import { useTranslations } from 'next-intl';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Card } from './card';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /** 'card' for a failed page or tab load; 'inline' inside modals and panels. */
  variant?: 'card' | 'inline';
  className?: string;
}

/** A failed load. Never render a fetch failure as data or as an empty list. */
export default function ErrorState({
  title,
  message,
  onRetry,
  retryLabel,
  variant = 'card',
  className,
}: ErrorStateProps) {
  const t = useTranslations('Common');

  if (variant === 'inline') {
    return (
      <div
        role="alert"
        className={cn(
          'bg-card border-border border-l-danger flex items-center gap-3 rounded-lg border border-l-4 px-4 py-3',
          className,
        )}
      >
        <ExclamationTriangleIcon className="text-danger h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="text-foreground min-w-0 flex-1 text-sm">
          {title ? <p className="font-semibold">{title}</p> : null}
          <p className={title ? 'text-muted' : undefined}>{message ?? t('errorStateTitle')}</p>
        </div>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {retryLabel ?? t('buttons.retry')}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Card className={className}>
      <div role="alert" className="p-8 text-center">
        <div className="bg-danger/10 border-danger/20 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border">
          <ExclamationTriangleIcon className="text-danger h-8 w-8" aria-hidden="true" />
        </div>
        <h3 className="mb-2 text-lg font-semibold">{title ?? t('errorStateTitle')}</h3>
        <p className="text-foreground/80 text-sm">{message ?? t('errorStateDescription')}</p>
        {onRetry ? (
          <div className="mt-4">
            <Button variant="outline" onClick={onRetry}>
              {retryLabel ?? t('buttons.retry')}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
